
import { of, Observable, from } from 'rxjs';
// Core
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Vendor
import * as _ from 'lodash';

// Leaflet
import * as L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-imageoverlay-rotated';
import 'leaflet-fullscreen';
import 'leaflet-draw-drag';
// import 'leaflet-snap';
import './lib/leaflet-draw-snap';
import 'leaflet-geometryutil';

// Ngx Leaflet

// Interfaces
import * as Interfaces from './interfaces';

// Other
import { environment } from '../../environments/environment';
import { LeafletControlLayersConfig } from '@bluehalo/ngx-leaflet';

// this makes typescript happy
declare global {
	interface Window {
		google?: any;
	}
}

//
@Injectable()
export class Service {
	// streets-v9
	// satellite-streets-v9
	// light-v9
	// dark-v9
	// outdoors-v9

	private tiles: { [id: string]: Interfaces.Tile } = {};

	public layers: { [id: string]: L.TileLayer } = {};
	private googleAutocompleteService: any = null;
	private static googleApiPromise;
	private geocodeService;

	/**
	 * This constructor will set the tiles.
	 */
	constructor(private http: HttpClient) {
		// set the tiles
		this.tiles = _.mapValues(environment.leaflet.tiles, (tile: Interfaces.Tile) => {
			// mutate
			if (tile.type === 'mapbox' && environment.geocode.mapbox.accessToken) {
				tile.url += '?access_token=' + environment.geocode.mapbox.accessToken;
			}

			return _.merge(tile, {
				layer: new L.TileLayer(tile.url, tile.options || {})
			});
		});

		this.layers = _.mapValues(this.tiles, 'layer');

		Service.loadGoogleApi().then(() => {
			return;
		}).catch((error) => {
			return;
		});
	}


	// dynamically load the google api
    public static loadGoogleApi() {
		if (!Service.googleApiPromise) { // Load once
			Service.googleApiPromise = new Promise((resolve) => {
				window['__onGapiLoaded'] = () => {
					resolve(window);
				};
				// Assemble maps API with API key
				const tag = 'googleMapsApi';
				let url = environment.geocode.googleMaps.url;
				url = url.replace('{{APIKEY}}', environment.geocode.googleMaps.apiKey);
	
				const node = document.createElement('script');
				node.src = url + '&callback=__onGapiLoaded';
				node.type = 'text/javascript';
				node.async = true;  
				node.defer = true;  
	
				document.getElementsByTagName('head')[0].appendChild(node);
			});
		}
	
		return Service.googleApiPromise;
	}
	

	public geocode(query: string, options?: any): Observable<any> {
		if(environment.geocode.provider === 'mapbox'){
			return this.mapBoxGeocode(query, options);
		}
		return this.googleAutocomplete(query, options);
	}

	// mapbox autocomplete geocoding
	private mapBoxGeocode(query: string, options?: any): Observable<any> {
		if (query && query.length > 0) {
			return this.http.get(environment.geocode.mapbox.geocodeUrl + query + '.json', {
				params: _.merge({
					access_token: environment.geocode.mapbox.accessToken
				}, options || {})
			});
		}

		return of([]);
	}

	// google autocomplete geocoding
	private googleAutocomplete(query: string, options?: any): Observable<any> {
		if (!window.google?.maps?.places || !query) {
			return of([]);
		}

		const request: any = {
			input: query,
			includedPrimaryTypes: ['geocode']
		};

		return from(
			window?.google?.maps?.places?.AutocompleteSuggestion
				.fetchAutocompleteSuggestions(request)
				.then((response: any) => {
					const suggestions = response?.suggestions || [];
					
					return {
						features: suggestions.map((s: any) => ({
							place_name: s?.placePrediction?.text?.text,
							placeId: s?.placePrediction?.placeId
						}))
					};
				})
				.catch(() => ({ features: [] }))
		);
	}
	
	// get latlon for a specific address
	public async getLocation(place_id: string): Promise<any> {
		if(!window.google){
			return Promise.resolve();
		}
		// load the google geocoder
		// note the google script is loaded in app.component.onInit
		if(!this.geocodeService){
			this.geocodeService = new window.google.maps.Geocoder();
		}

		var request = {
			placeId: place_id
		};
		
		return new Promise((resolve, reject) => {
			this.geocodeService.geocode(request, function(result, status) {
				if(status === 'OK'){
					const loc = result[0].geometry.location.toJSON();
					const geom = {
						type: 'Point',
						coordinates: [loc.lng, loc.lat]
					}
					resolve(geom);
				} else {
					reject(status);
				}
			});
		}).catch((error) => {
			console.log('Errror getting location detail. msg: ' + error.message);
		});
	}

	public get options(): L.MapOptions {
		return {
			fullscreenControl: {
				pseudoFullscreen: true
			},
			attributionControl: false,
			// scrollWheelZoom: false,
			zoom: this.perimeterZoom,
			layers: [
				this.layers.Street
			]
		};
	}

	public get buildingOptions(): L.MapOptions {
		return {
			fullscreenControl: {
				pseudoFullscreen: true
			},
			attributionControl: false,
			// scrollWheelZoom: false,
			zoom: this.perimeterZoom,
			layers: [
				this.layers.Satellite
			]
		};
	}

	public get floorOptions(): L.MapOptions {
		return {
			fullscreenControl: {
				pseudoFullscreen: true
			},
			attributionControl: false,
			// scrollWheelZoom: false,
			zoom: this.perimeterZoom,
			layers: [
				this.layers.Street
			]
		};
	}

	public get roomOptions(): L.MapOptions {
		return {
			fullscreenControl: {
				pseudoFullscreen: true
			},
			attributionControl: false,
			// scrollWheelZoom: false,
			zoom: this.perimeterZoom,
			layers: [
				this.layers.Street
			]
		};
	}

	public get layersControl(): LeafletControlLayersConfig {
		return {
			baseLayers: this.layers,
			overlays: {}
		};
	}

	public get layersControlOptions(): any {
		return {
			position: 'topleft',
			autoZIndex: false
		};
	}

	public get perimeterZoom(): number {
		return 20;
	}

	public get snapDistance(): number {
		return 5;
	}

	/**
	 * The default icon representing a point.
	 *
	 * @return - The icon representing the point.
	 */
	public get icon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png'
		}));
	}

	/**
	 * The default icon the top left of a floorplan.
	 *
	 * @return - The icon representing the point.
	 */
	public get floorplanTopLeftIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'FLOORPLAN_TOP_LEFT',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-top-left.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-top-left.png',
			iconSize: [42, 42],
			shadowUrl: null,
			shadowRetinaUrl: null,
			shadowSize: null,
			shadowAnchor: null
		}));
	}

	/**
	 * The default icon the top right of a floorplan.
	 *
	 * @return - The icon representing the point.
	 */
	public get floorplanTopRightIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'FLOORPLAN_TOP_RIGHT',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-top-right.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-top-right.png',
			iconSize: [42, 42],
			shadowUrl: null,
			shadowRetinaUrl: null,
			shadowSize: null,
			shadowAnchor: null
		}));
	}

	/**
	 * The default icon the bottom left of a floorplan.
	 *
	 * @return - The icon representing the point.
	 */
	public get floorplanBottomLeftIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'FLOORPLAN_BOTTOM_LEFT',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-bottom-left.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-bottom-left.png',
			iconSize: [42, 42],
			shadowUrl: null,
			shadowRetinaUrl: null,
			shadowSize: null,
			shadowAnchor: null
		}));
	}

	/**
	 * The default icon representing an instactive safezone.
	 *
	 * @return - The leaflet icon representing the safezone.
	 */
	public get safezoneInactiveIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-grey.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-grey.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png',
			className: 'SAFEZONE'
		}));
	}

	/**
	 * The default icon representing a active safezone.
	 *
	 * @return - The leaflet icon representing the safezone.
	 */
	public get safezoneActiveIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'SAFEZONE',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-green.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-green.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png'
		}));
	}

	/**
	 * The default icon representing a beacon.
	 *
	 * @return - The leaflet icon representing the beacon.
	 */
	public get beaconIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'BEACON',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-blue.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-blue.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png'
		}));
	}

	/**
	 * The default icon representing a sensor.
	 *
	 * @return - The leaflet icon representing the sensor.
	 */
	public get sensorIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'SENSOR',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-orange.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-orange.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png'
		}));
	}

	/**
	 * The default icon representing the origin of a graph.
	 *
	 * @return - The leaflet icon representing the origin.
	 */
	public get originIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'ORIGIN',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-origin.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-origin.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png'
		}));
	}

	/**
	 * The default icon representing the X axis of a graph.
	 *
	 * @return - The leaflet icon representing the X.
	 */
	public get xIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'X',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-x.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-x.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png'
		}));
	}

	/**
	 * The default icon representing the X axis of a graph.
	 *
	 * @return - The leaflet icon representing the X.
	 */
	public get yIcon(): L.Icon {
		return L.icon(_.merge(_.cloneDeep(L.Icon.Default.prototype.options), {
			className: 'Y',
			iconRetinaUrl: 'assets/vendors/base/images/leaflet/marker-icon-2x-y.png',
			iconUrl: 'assets/vendors/base/images/leaflet/marker-icon-y.png',
			shadowUrl: 'assets/vendors/base/images/leaflet/marker-shadow.png'
		}));
	}

	/**
	 * Gets the color with the associated rating of a geofence.
	 *
	 * @param rating - The rating of the geofence.
	 * @return - The associated color for that rating.
	 */
	public geofenceRatingColor(rating: string) {
		switch (rating) {
			case 'HIGH':
				return 'red';
			case 'MEDIUM':
				return 'orange';
			case 'LOW':
				return 'yellow';
		}
	}

	/**
	 * Gets the color with the associated status of a safezone.
	 *
	 * @param active - True if the safezone is active.
	 * @return - The associated color for that safezone.
	 */
	public safezoneColor(active?: boolean) {
		if (active) {
			return '#52E74B';
		}

		return '#ACBCAB';
	}

	/**
	 * Gets the safezone icon with the status of the safezone.
	 *
	 * @param active - True if the safezone is active.
	 * @return - The associated icon for the safezone.
	 */
	public safezoneIcon(active?: boolean) {
		if (active) {
			return this.safezoneActiveIcon;
		}

		return this.safezoneInactiveIcon;
	}


}
